import { ChevronDownIcon, ChevronUpIcon, UserIcon } from "@xola/icons";
import { Button, Checkbox, Input, PopoverList, Select } from "@xola/ui-kit";
import clsx from "clsx";
import { orderBy, round } from "lodash";
import React, { useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { LineItem, LineItemObject, PaymentLineItem, PaymentMethod, Purchase } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { getAllowedPaymentMethods, getExcludedPaymentMethods, getPaymentMethodName } from "../../../models/Payment";
import { getCustomerName, getOriginalPaymentMethod } from "../../../models/Purchase";
import { Currency } from "../../Common/Currency";
import { CurrencyInput } from "../../Input/CurrencyInput";
import { Loader } from "../../Loader";
import "./EventBookingsTable.css";
import { NoTranslate } from "../../Common/NoTranslate";

const enum sortOrder {
    ASC = "asc",
    DESC = "desc",
}

export const enum RefundTerm {
    FULL = "full",
    PARTIAL = "partial",
    NONE = "none",
}

export const enum RefundMethod {
    ORIGINAL = "original",
    STORE_CREDIT = "store_credit",
}

interface EventBookingsTableProps {
    isLoading: boolean;
    purchasesMap: { [key: string]: Purchase };
    values: CancelBookingsFormValues;
    errors: any;
    onChange: (values: CancelBookingFormPurchaseValues[]) => void;
    onFieldChange: (index: number, field: string, value: any) => void;
    onRowChange: (index: number, value: any) => void;
}

export interface CancelBookingsFormValues {
    reason: string;
    notify: boolean;
    removeAffiliates: boolean;
    removeGuides: boolean;
    blackoutEvent: boolean;
    purchases: CancelBookingFormPurchaseValues[];
}

export interface CancelBookingFormPurchaseValues {
    id: ID;
    customerName: string;
    amountPaid: number;
    isSelected: boolean;
    refundTerm: string;
    refundAmount: number;
    refundMethod: string;
}

export const EventBookingsTable = ({
    isLoading,
    purchasesMap,
    values,
    errors,
    onChange,
    onFieldChange,
    onRowChange,
}: EventBookingsTableProps) => {
    const seller = useSeller();
    const [currentSort, setCurrentSort] = useState<sortOrder>(sortOrder.ASC);
    const sortCustomers = () => {
        setCurrentSort(currentSort === sortOrder.ASC ? sortOrder.DESC : sortOrder.ASC);
        const sortedValues = orderBy(values.purchases, ["customerName"], [currentSort]);
        onChange(sortedValues);
    };

    /**
     *  Handles Changes occurred in Single Field
     */
    const handleChange = (index: number, field: string, value: any) => {
        onFieldChange(index, field, value);
    };

    /**
     *  Handles Changes to the RefundTerm field.
     *  This needs a separate function since it also has to update refundAmount
     *  we cannot have two onFieldChange functions since one will trigger
     *  the re-render before the other
     */
    const handleRefundTermChange = (index: number, value: string) => {
        const row = { ...values.purchases[index] };
        row.refundTerm = value;
        if (value === RefundTerm.FULL) {
            row.refundAmount = row.amountPaid;
        } else if (value === RefundTerm.NONE) {
            row.refundAmount = 0;
        }

        onRowChange(index, row);
    };

    const handleGroupRefundTermChange = (refundTerm: string, refundPercentage: number) => {
        const updatedValues = [...values.purchases];
        for (const value of updatedValues) {
            value.refundTerm = refundTerm;
            if (refundTerm === RefundTerm.PARTIAL) {
                value.refundAmount = round((refundPercentage * value.amountPaid) / 100, 2);
            } else if (refundTerm === RefundTerm.FULL) {
                value.refundAmount = value.amountPaid;
            } else {
                value.refundAmount = 0;
            }
        }

        onChange(updatedValues);
    };

    const handleGroupRefundMethodChange = (refundMethod: string) => {
        const updatedValues = [...values.purchases];
        for (const value of updatedValues) {
            const originalMethod = getOriginalPaymentMethod(seller, purchasesMap[value.id]);
            const method = refundMethod === RefundMethod.ORIGINAL ? originalMethod.id : refundMethod;
            value.refundMethod = method;
        }

        onChange(updatedValues);
    };

    const handleSelectAll = () => {
        const updatedValues = [...values.purchases];
        const totalCount = updatedValues.length;
        const selectedCount = updatedValues.filter((value) => value.isSelected).length;
        for (const value of updatedValues) {
            value.isSelected = selectedCount !== totalCount;
        }

        onChange(updatedValues);
    };

    return isLoading ? (
        <Loader />
    ) : (
        <div className="leading-p1 tracking-tightest text-gray-darker">
            <div className="grid-template-columns thead grid items-center gap-8 bg-gray-lighter py-2 px-4 text-sm font-bold text-black">
                <div className="space-x-1.5">
                    <span>Name</span>
                    <span onClick={sortCustomers}>
                        {currentSort === sortOrder.ASC ? <ChevronDownIcon /> : <ChevronUpIcon />}
                    </span>
                </div>
                <div>Purchase Value</div>
                <div>Amount Paid</div>
                <div>Payment Method</div>
                <div className="flex items-center space-x-1.5">
                    <span>Refund Term</span> <RefundTermPopover onChange={handleGroupRefundTermChange} />
                </div>
                <div>Refund Value</div>
                <div className="flex items-center space-x-1.5">
                    <span>Refund Method</span>
                    <RefundMethodPopover onChange={handleGroupRefundMethodChange} />
                </div>
                <div className="text-center">
                    <span className="underline hover:cursor-pointer" onClick={handleSelectAll}>
                        Select All
                    </span>
                </div>
            </div>
            <Virtuoso
                style={{ height: 250 }}
                data={values.purchases}
                itemContent={(index, value) => {
                    const purchase = purchasesMap[value.id];
                    return (
                        <TableRow
                            index={index}
                            value={value}
                            purchase={purchase}
                            errors={errors?.[index]}
                            handleChange={handleChange}
                            handleRefundTermChange={handleRefundTermChange}
                        />
                    );
                }}
            />
        </div>
    );
};

interface RefundTermPopoverProps {
    onChange: (refundTerm: string, refundPercentage: number) => void;
}

const RefundTermPopover = ({ onChange }: RefundTermPopoverProps) => {
    const [visible, setVisible] = useState(false);
    const [refundPercentage, setRefundPercentage] = useState<number>(90);
    const onClickItem = (event_: any, element: string) => {
        onChange(element, refundPercentage);
        setVisible(false);
    };

    return (
        <PopoverList visible={visible} className="w-75" distance={18}>
            <ChevronDownIcon
                onClick={() => {
                    setVisible(!visible);
                }}
            />
            <PopoverList.Item name={RefundTerm.FULL} onClickItem={onClickItem}>
                <span>Full</span>
            </PopoverList.Item>
            <PopoverList.Item name={RefundTerm.PARTIAL} className="justify-between py-1 px-2" onClickItem={() => null}>
                <span className="py-3 px-2">Partial</span>
                <div className="flex items-center space-x-1.5">
                    <span className="flex items-center bg-gray-lighter">
                        <Input
                            className="w-15"
                            type="number"
                            value={refundPercentage ? refundPercentage : null}
                            onChange={(e: any) => {
                                setRefundPercentage(e.target.value);
                            }}
                        />
                        <span className="w-7 text-center">%</span>
                    </span>
                    <Button
                        onClick={(e: any) => {
                            onClickItem(e, RefundTerm.PARTIAL);
                        }}
                    >
                        Apply
                    </Button>
                </div>
            </PopoverList.Item>
            <PopoverList.Item name={RefundTerm.NONE} onClickItem={onClickItem}>
                <span>None</span>
            </PopoverList.Item>
        </PopoverList>
    );
};

interface RefundMethodPopoverProps {
    onChange: (refundTerm: string) => void;
}

const RefundMethodPopover = ({ onChange }: RefundMethodPopoverProps) => {
    const seller = useSeller();
    const [visible, setVisible] = useState(false);
    const hide = () => {
        setVisible(false);
    };

    const show = () => {
        setVisible(true);
    };

    const onClickItem = (_: any, element: string) => {
        onChange(element);
        setVisible(false);
    };

    /** excluding credit card from the table header bulk selector
     * since for some purchases credit card will not always be available to refund
     */
    const excludePaymentMethods = [PaymentMethod.CREDIT_CARD];
    const paymentMethods = getAllowedPaymentMethods({
        seller,
        isRefund: true,
        isInvoiceAllowed: false,
        excludePaymentMethods,
    });

    return (
        <PopoverList distance={18} visible={visible} onClickOutside={hide}>
            <ChevronDownIcon onClick={visible ? hide : show} />
            <PopoverList.Item name={RefundMethod.ORIGINAL} onClickItem={onClickItem}>
                <span>Original Method of Payment</span>
            </PopoverList.Item>
            {paymentMethods.map((paymentMethod) => (
                <PopoverList.Item key={paymentMethod.id} name={paymentMethod.id} onClickItem={onClickItem}>
                    <span>{paymentMethod.name}</span>
                </PopoverList.Item>
            ))}
        </PopoverList>
    );
};

export const getPaymentMethod = (lineItems: LineItem[]): PaymentMethod | ID => {
    const lineItem = lineItems.find((lineItem: LineItem) => {
        return lineItem.object === LineItemObject.PAYMENT;
    });
    const paymentLineItem = lineItem as PaymentLineItem;
    return paymentLineItem?.payment?.method ?? PaymentMethod.CASH;
};

interface TableRowProps {
    index: number;
    purchase: Purchase;
    value: any;
    errors: any;
    handleChange: (index: number, field: string, value: any) => void;
    handleRefundTermChange: (index: number, value: any) => void;
}

const TableRow = ({ index, value, purchase, errors, handleChange, handleRefundTermChange }: TableRowProps) => {
    const seller = useSeller();
    const originalMethod = getOriginalPaymentMethod(seller, purchase);
    const excludePaymentMethods = getExcludedPaymentMethods(purchase);
    const paymentMethods = getAllowedPaymentMethods({
        seller,
        isRefund: true,
        isInvoiceAllowed: false,
        excludePaymentMethods,
    });

    return (
        <div
            key={purchase.id}
            className="grid-template-columns tbody grid items-center gap-8 border-b border-gray-light py-3 px-4"
            data-testid="event-bookings-table-item"
        >
            <div>
                <NoTranslate as="div" data-testid="event-bookings-table-customer-name">
                    {getCustomerName(purchase)}
                </NoTranslate>
                <div className="space-x-1.5" data-testid="event-bookings-table-reserved">
                    <UserIcon />
                    <span>
                        <var data-var="reservedCount">{purchase.items[0].quantity}</var> Reserved
                    </span>
                </div>
            </div>
            <div data-testid="event-bookings-table-purchase-item-amount">
                <Currency>{purchase.items[0].amount}</Currency>
            </div>
            <div data-testid="event-bookings-table-paid">
                <Currency>{value.amountPaid}</Currency>
            </div>
            <div data-testid="event-bookings-table-payment-method">
                {getPaymentMethodName(seller, getPaymentMethod(purchase.lineItems))}
            </div>
            <div className="w-[85px]">
                <Select
                    name="refundTerm"
                    value={value.refundTerm}
                    onChange={(e: any) => {
                        handleRefundTermChange(index, e.target.value);
                    }}
                >
                    <option value={RefundTerm.FULL}>Full</option>
                    <option value={RefundTerm.PARTIAL}>Partial</option>
                    <option value={RefundTerm.NONE}>None</option>
                </Select>
            </div>
            <div data-testid="event-bookings-table-refund-amount">
                {[RefundTerm.NONE, RefundTerm.FULL].includes(value.refundTerm) ? (
                    <Currency>{value.refundAmount}</Currency>
                ) : (
                    <CurrencyInput
                        disabled={value.refundTerm === RefundTerm.NONE}
                        value={value.refundAmount}
                        title={errors?.refundAmount}
                        className={clsx(errors?.refundAmount && "border-danger", "w-[80px]")}
                        onValueChange={({ floatValue }) => {
                            handleChange(index, "refundAmount", floatValue);
                        }}
                    />
                )}
            </div>
            <div className="w-[150px]">
                <Select
                    name="refundMethod"
                    value={value.refundMethod}
                    className="pr-7"
                    onChange={(e: any) => {
                        handleChange(index, "refundMethod", e.target.value);
                    }}
                >
                    {paymentMethods.map((paymentMethod) => {
                        const isOriginalPaymentMethod = paymentMethod.id === originalMethod.id;
                        const name = `${paymentMethod.name} ${isOriginalPaymentMethod ? "(Original)" : ""}`;

                        return (
                            <option
                                key={paymentMethod.id}
                                value={paymentMethod.id}
                                data-testid="event-bookings-table-refund-Method"
                            >
                                {name}
                            </option>
                        );
                    })}
                </Select>
            </div>
            <div className="flex justify-center" data-testid="event-bookings-table-select-all-checkbox">
                <Checkbox
                    checked={value.isSelected}
                    onChange={(e: any) => {
                        handleChange(index, "isSelected", e.target.checked);
                    }}
                />
            </div>
        </div>
    );
};
